import { RefreshCw } from 'lucide-react'
import { Component, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { StatePanel } from '@/components/ui/StatePanel'
import { useT } from '@/i18n'
import { forceReload, isChunkLoadError } from '@/lib/chunkReload'

type Props = {
  children: ReactNode
  /**
   * Runs before the children are rebuilt. Use it to drop whatever failed
   * permanently — a cached module, a rejected lazy() — so a retry is a real
   * second attempt rather than the same failure replayed.
   */
  onRetry?: () => void
}

type State = { error: Error | null }

/**
 * Contains a failure to one page: the sidebar and topbar stay usable, so the
 * user can open another module instead of reloading into the same dead chunk.
 *
 * A chunk that cannot be fetched is reported as "could not be loaded" rather
 * than "new version available" — a deploy replaces the file, but so do a lost
 * connection and a blocked request, and only the first is a new version.
 *
 * Errors are not reported here: React 19's root onCaughtError (sentryRootOptions
 * in lib/sentry.ts) already reports everything a boundary catches.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  private retry = () => {
    this.props.onRetry?.()
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    return <PageErrorState error={error} onRetry={this.retry} />
  }
}

function PageErrorState({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const t = useT()
  const loadFailed = isChunkLoadError(error)
  return (
    <StatePanel
      kind={loadFailed ? 'unavailable' : 'error'}
      title={loadFailed ? t('errors.states.chunkTitle') : t('errors.states.crashTitle')}
      description={loadFailed ? t('errors.states.chunkText') : t('errors.states.crashText')}
      action={
        <div className="flex gap-2">
          <Button variant="primary" icon={<RefreshCw className="size-3.5" />} onClick={onRetry}>
            {t('common.actions.tryAgain')}
          </Button>
          <Button onClick={forceReload}>{t('errors.states.reload')}</Button>
        </div>
      }
    />
  )
}